import { useAuth0 } from '@auth0/auth0-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from '@dfh-corp-sites/ui'
import React, { useEffect, useState } from 'react'

import { OnboardRequestDocument } from '../../graphql/search'
import { retryOnboardRequest } from '../../lib/onboarding/request'
import {
  setOnboardingRequestState,
  useOnboardingRequestState,
} from '../../lib/onboarding/state/request-state'
import { getActiveOnboarding } from '../../lib/onboarding/utils'
import { useSearchLazyQuery } from '../../lib/search'
import { userClaimIds } from '../../utils/userClaims'

const AccountSetupErrorModal: React.FC = () => {
  const [canOpen, setCanOpen] = useState(true)
  const [isRetrying, setIsRetrying] = useState(false)

  const { user } = useAuth0()
  const { name: onboardingRequestState } = useOnboardingRequestState()
  const isAccountSetupError = onboardingRequestState === 'signup_error'
  const isLoading = onboardingRequestState === 'signup_loading'

  const [executeOnboardRequest] = useSearchLazyQuery(OnboardRequestDocument, {
    onCompleted: ({ onboardRequest }) => {
      if (onboardRequest?.status === 'FAILED') {
        setOnboardingRequestState({
          name: 'signup_error',
          error: new Error(onboardRequest.response || 'Unknown error during onboarding request'),
        })
      }
    },
  })

  const handleRetry = () => {
    setIsRetrying(true)
    retryOnboardRequest()
  }

  useEffect(() => {
    const activeOnboarding = getActiveOnboarding(user)
    const customerId = user?.[userClaimIds.netSuiteId]
    if (!customerId || !activeOnboarding?.claim || onboardingRequestState !== 'initial') {
      return
    }

    executeOnboardRequest()
  }, [user, executeOnboardRequest, onboardingRequestState])

  useEffect(() => {
    if (isLoading && isRetrying) {
      setCanOpen(false)
    } else if (!isLoading && isRetrying) {
      setIsRetrying(false)
      if (onboardingRequestState === 'signup_error') {
        setCanOpen(true)
      }
    }
  }, [isLoading, isRetrying, onboardingRequestState])

  return (
    <Dialog
      open={canOpen && isAccountSetupError}
      onOpenChange={(open) => {
        if (!open && !isRetrying) {
          return
        }
        setCanOpen(open)
      }}
    >
      <DialogContent className="max-w-2xl py-1">
        <DialogHeader className="text-2xl text-brand-navy font-medium mb-8 mt-10 px-2.5 md:px-5 xl:px-[30px]">
          Account setup was interrupted
        </DialogHeader>
        <div className="px-2.5 md:px-5 xl:px-[30px] mb-8">
          <DialogDescription>
            <strong>We&apos;re sorry for the inconvenience!</strong> It looks like we weren&apos;t
            able to complete your account setup.
          </DialogDescription>
          <p className="mt-4">
            Please contact technical support at{' '}
            <a
              className="text-brand-orange underline"
              href="mailto:techsupport@designsforhealth.com"
            >
              techsupport@designsforhealth.com
            </a>{' '}
            to ensure you can access all the benefits of your Designs for Health account.
          </p>
        </div>
        <DialogFooter className="px-2.5 md:px-5 xl:px-[30px] pb-8">
          <Button onClick={handleRetry} disabled={isRetrying} className="min-w-[120px]">
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AccountSetupErrorModal
